import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { User } from 'services/auth/auth.types';
import { useGetUserProfileMutation } from 'services/user/user.endpoint';
import {
    useGetUserProjectEventsMutation,
    useUpdateEventCommentMutation,
    useUpdateEventNextMeetingPreparationMutation
} from 'services/person/event/event.endpoint';
import { useGetAllAppointmentsMutation } from 'services/appointment/appointment.endpoint';

export type UnifiedItem = {
    id: number;
    source: 'event' | 'appointment';
    title: string;
    date: string;          // ISO
    status?: 'pending' | 'refused' | 'accepted';
    raw: any;
};

const PAGE_SIZE = 3;

export default function useMyAppointmentsVM() {
    const [me, setMe] = useState<User | null>(null);
    const [items, setItems] = useState<UnifiedItem[]>([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [selected, setSelected] = useState<UnifiedItem | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const [getMe] = useGetUserProfileMutation();
    const [getEvents] = useGetUserProjectEventsMutation();
    const [getAppointments] = useGetAllAppointmentsMutation();
    const [updateEventComment] = useUpdateEventCommentMutation();
    const [updateEventNextPrep] = useUpdateEventNextMeetingPreparationMutation();

    useEffect(() => {
        (async () => {
            const meRes = await getMe(undefined).unwrap();
            setMe(meRes.data);
            const projectId = meRes.data.projects?.[0]?.id;
            if (!projectId) {
                setIsLoading(false);
                return;
            }

            const [eventsRes, apptsRes] = await Promise.all([
                getEvents({ id: projectId } as any).unwrap(),
                getAppointments({ project_id: String(projectId) } as any).unwrap()
            ]);

            const events: UnifiedItem[] = (eventsRes.data ?? []).map((e: any) => ({
                id: e.id,
                source: 'event',
                title: e.label,
                date: e.started_at,
                raw: e
            }));

            const appointments: UnifiedItem[] = (apptsRes.data ?? [])
                .filter((a: any) => a.status === 'accepted' || a.status === 'refused')
                .map((a: any) => ({
                    id: a.id,
                    source: 'appointment',
                    title: a.label,
                    date: a.started_at ?? a.created_at,
                    status: a.status,
                    raw: a
                }));

            const merged = [...events, ...appointments].sort((a, b) =>
                moment(b.date).valueOf() - moment(a.date).valueOf()
            );

            setItems(merged);
            const now = moment().startOf('day');
            const upcomingFirst = [...merged]
                .filter((i) => i.status !== 'refused' && moment(i.date).isSameOrAfter(now, 'day'))
                .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())[0];
            const defaultSelected = upcomingFirst ?? merged[0] ?? null;
            if (defaultSelected) setSelected(defaultSelected);
            setIsLoading(false);
        })();
    }, [getMe, getEvents, getAppointments]);

    const nextAppointment = useMemo(() => {
        const now = moment();
        return [...items]
            .filter((i) => i.status !== 'refused' && moment(i.date).isSameOrAfter(now, 'day'))
            .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())[0] ?? null;
    }, [items]);

    const formatDate = (date: string) => moment(date).format('D MMMM YYYY');
    const pastItems = useMemo(() => {
        const today = moment().startOf('day');
        // Historique : éléments passés + toutes les demandes refusées (affichées avec leur badge),
        // même si leur date demandée est dans le futur.
        return items.filter((i) => i.status === 'refused' || moment(i.date).isBefore(today, 'day'));
    }, [items]);
    const visibleItems = pastItems.slice(0, visibleCount);
    const hasMore = pastItems.length > visibleCount;
    const loadMore = () => setVisibleCount((c) => c + PAGE_SIZE);

    const handleSaveEventComment = async (value: string) => {
        if (!selected || selected.source !== 'event') return;
        const projectId = me?.projects?.[0]?.id;
        if (!projectId) return;
        const res = await updateEventComment({ projectId, eventId: selected.id, user_comment: value }).unwrap();
        applyEventUpdate(res.data);
    };

    const handleSaveNextMeetingPrep = async (value: string) => {
        if (!selected || selected.source !== 'event') return;
        const projectId = me?.projects?.[0]?.id;
        if (!projectId) return;
        const res = await updateEventNextPrep({ projectId, eventId: selected.id, user_next_meeting_preparation: value }).unwrap();
        applyEventUpdate(res.data);
    };

    const applyEventUpdate = (updated: any) => {
        setItems((prev) => prev.map((i) =>
            i.source === 'event' && i.id === updated.id ? { ...i, raw: { ...i.raw, ...updated } } : i
        ));
        setSelected((prev) => prev && prev.source === 'event' && prev.id === updated.id
            ? { ...prev, raw: { ...prev.raw, ...updated } }
            : prev
        );
    };

    // Le "dernier RDV" pour la section "Préparation au prochain entretien"
    // = l'élément le plus récent qui est un event (pas une demande)
    const lastEvent = items.find((i) => i.source === 'event');
    const isLastEvent = selected && lastEvent && selected.source === 'event' && selected.id === lastEvent.id;

    return {
        me,
        items: visibleItems,
        totalCount: pastItems.length,
        hasMore,
        loadMore,
        selected,
        setSelected,
        formatDate,
        isLoading,
        handleSaveEventComment,
        handleSaveNextMeetingPrep,
        isLastEvent,
        nextAppointment
    };
}
